import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import { CreditSafePortfolioUpdate } from '@dill/dill-shared/dist/src';

export const getNACMCompanyLookupList = createAsyncThunk(
  'creditReport/getNACMCompanyLookupList',
  async (
    {
      supplierId,
      companyDetails,
    }: {
      supplierId: string;
      companyDetails: {
        companyName: string;
        address?: string;
        city?: string;
        state?: string;
        telephone?: string;
        zipCode?: string;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getNACMCompanyLookupList`,
        { ...companyDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getNACMReport = createAsyncThunk(
  'creditReport/getNACMReport',
  async (
    {
      supplierId,
      companyNACMID,
      name,
      type,
      creditApplicationId,
      internalFormSubmissionId,
    }: {
      supplierId: string;
      companyNACMID: string;
      name: string;
      type: string;
      creditApplicationId: string;
      // Pulled from an internal form's review page instead of a credit application.
      internalFormSubmissionId?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getNACMReport`,
        { companyNACMID, name, type, creditApplicationId, internalFormSubmissionId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getExperianBusinessSearch = createAsyncThunk(
  'creditReport/getExperianBusinessSearch',
  async (
    {
      supplierId,
      companyDetails,
    }: {
      supplierId: string;
      companyDetails: {
        companyName: string;
        city?: string;
        state?: string;
        street?: string;
        zip?: string;
        phone?: string;
        taxId?: string;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getExperianBusinessSearch`,
        { ...companyDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getExperianPremierProfile = createAsyncThunk(
  'creditReport/getExperianPremierProfile',
  async (
    {
      supplierId,
      experianBin,
      name,
      type,
      creditApplicationId,
      internalFormSubmissionId,
      street,
      city,
      state,
      zip,
      phone,
      taxId,
    }: {
      supplierId: string;
      experianBin: string;
      name: string;
      type: string;
      creditApplicationId: string;
      // Pulled from an internal form's review page instead of a credit application.
      internalFormSubmissionId?: string;
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      phone?: string;
      taxId?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/getExperianPremierProfile`,
        {
          experianBin,
          name,
          type,
          creditApplicationId,
          internalFormSubmissionId,
          street,
          city,
          state,
          zip,
          phone,
          taxId,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const creditSafeCompanySearch = createAsyncThunk(
  'creditReport/creditSafeCompanySearch',
  async (
    {
      supplierId,
      companyDetails,
    }: {
      supplierId: string;
      companyDetails: {
        companyName: string;
        address?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        page?: number;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/companySearch`,
        { ...companyDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getCreditSafeReport = createAsyncThunk(
  'creditReport/getCreditSafeReport',
  async (
    {
      supplierId,
      connectId,
      name,
      type,
      creditApplicationId,
      internalFormSubmissionId,
    }: {
      supplierId: string;
      connectId: string;
      name: string;
      type: string;
      creditApplicationId: string;
      // Pulled from an internal form's review page instead of a credit application.
      internalFormSubmissionId?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/companyCreditReport`,
        { connectId, name, type, creditApplicationId, internalFormSubmissionId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const directorSearch = createAsyncThunk(
  'creditReport/directorSearch',
  async (
    {
      supplierId,
      companyDetails,
    }: {
      supplierId: string;
      companyDetails: {
        firstName: string;
        lastName: string;
        page?: number;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/directorSearch`,
        { ...companyDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const directorReport = createAsyncThunk(
  'creditReport/directorReport',
  async (
    {
      supplierId,
      peopleId,
    }: {
      supplierId: string;
      peopleId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/directorReport`,
        { peopleId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createMonitoringPortfolio = createAsyncThunk(
  'creditReport/createMonitoringPortfolio',
  async (
    {
      supplierId,
      details,
    }: {
      supplierId: string;
      details: {
        name: string;
        isDefault: boolean;
        emails: {
          firstName: string;
          lastName: string;
          emailAddress: string;
        }[];
        emailSubject: string;
        emailLanguage: string;
        frequency: string;
      };
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/createMonitoringPortfolio`,
        { ...details },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSavedMonitoringPortfolios = createAsyncThunk(
  'creditReport/getSavedMonitoringPortfolios',
  async (
    {
      supplierId,
    }: {
      supplierId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/getSavedMonitoringPortfolios`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateSavedMonitoringPortfolio = createAsyncThunk(
  'creditReport/updateSavedMonitoringPortfolio',
  async (
    {
      supplierId,
      portfolioId,
      updatedDetails,
    }: {
      supplierId: string;
      portfolioId: string;
      updatedDetails: CreditSafePortfolioUpdate;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/updateSavedMonitoringPortfolio/${portfolioId}`,
        {
          ...updatedDetails,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deleteSavedMonitoringPortfolio = createAsyncThunk(
  'creditReport/deleteSavedMonitoringPortfolio',
  async (
    {
      supplierId,
      portfolioId,
    }: {
      supplierId: string;
      portfolioId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/deleteSavedMonitoringPortfolio/${portfolioId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const uploadManualCreditReport = createAsyncThunk(
  'creditReport/uploadManualCreditReport',
  async (
    {
      supplierId,
      creditApplicationId,
      internalFormSubmissionId,
      fileName,
      fileUrl,
      address1,
      address2,
      city,
      state,
      zipCode,
    }: {
      supplierId: string;
      creditApplicationId: string;
      // Pulled from an internal form's review page instead of a credit application.
      internalFormSubmissionId?: string;
      fileName: string;
      fileUrl: string;
      address1?: string;
      address2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/uploadManualCreditReport`,
        {
          creditApplicationId,
          internalFormSubmissionId,
          fileName,
          fileUrl,
          address1,
          address2,
          city,
          state,
          zipCode,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const deleteManualCreditReport = createAsyncThunk(
  'creditReport/deleteManualCreditReport',
  async (
    {
      supplierId,
      creditApplicationId,
      internalFormSubmissionId,
      reportId,
    }: {
      supplierId: string;
      creditApplicationId: string;
      // Pulled from an internal form's review page instead of a credit application.
      internalFormSubmissionId?: string;
      reportId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditApplications/deleteManualCreditReport`,
        { creditApplicationId, internalFormSubmissionId, reportId },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSavedCreditSafeCompanies = createAsyncThunk(
  'creditReport/getSavedCreditSafeCompanies',
  async (
    {
      supplierId,
    }: {
      supplierId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/creditSafe/getSavedCreditSafeCompanies`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
